import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { CarriedAnswerView, QualificationView } from "@/lib/types";
import { Card, CardTitle } from "@/components/Card";
import { formatDateTime } from "@/lib/format";

/** Render an extracted JSON answer for a human: booleans as Yes/No, scalars as text. */
function displayAnswer(answer: unknown): string {
  if (answer === null || answer === undefined) return "—";
  if (typeof answer === "boolean") return answer ? "Yes" : "No";
  if (typeof answer === "string") return answer;
  if (typeof answer === "number") return String(answer);
  return JSON.stringify(answer);
}

/**
 * What the agent has learned about this lead in its current phase, and what it
 * is still asking. Renders nothing when the phase declares no questions and no
 * earlier answers exist — most phases ask nothing, and an empty card is noise.
 */
export default function QualificationCard({ prospectId }: { prospectId: string }) {
  const q = useQuery({
    queryKey: ["prospect-qualification", prospectId],
    queryFn: () => api.get<QualificationView>(`/api/v1/prospects/${prospectId}/qualification`),
  });

  if (!q.data) return null;

  // Defensive against a partial payload; hides rather than crashes.
  const questions = q.data.questions ?? [];
  const carried = q.data.carriedAnswers ?? [];
  const totalCount = q.data.totalCount ?? questions.length;
  const answeredCount = q.data.answeredCount ?? 0;
  if (totalCount === 0 && carried.length === 0) return null;

  const pct = totalCount > 0 ? Math.round((answeredCount / totalCount) * 100) : 0;

  return (
    <Card>
      <CardTitle>
        Qualification — {answeredCount}/{totalCount} answered
      </CardTitle>

      {totalCount > 0 ? (
        <div
          className="h-1.5 rounded-full bg-zinc-100 mb-3"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={totalCount}
          aria-valuenow={answeredCount}
        >
          <div
            className="h-1.5 rounded-full bg-blue-500"
            style={{ width: `${pct}%` }}
          />
        </div>
      ) : null}

      <ul className="divide-y divide-[var(--border)]">
        {questions.map((question) => (
          <li key={question.key} className="py-2 first:pt-0 last:pb-0">
            <div className="text-sm">{question.prompt}</div>
            {question.answered ? (
              <>
                <div className="text-sm font-medium">{displayAnswer(question.answer)}</div>
                {question.extractedAt ? (
                  <div className="text-xs text-[var(--fg-muted)]">
                    Extracted {formatDateTime(question.extractedAt)}
                  </div>
                ) : null}
              </>
            ) : (
              <div className="text-sm text-[var(--fg-muted)]">
                Not yet answered
                {question.required ? (
                  <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-amber-800">
                    required
                  </span>
                ) : null}
              </div>
            )}
          </li>
        ))}
      </ul>

      {carried.length > 0 ? (
        <div className="mt-3 border-t border-[var(--border)] pt-3">
          <div className="mb-2 text-xs uppercase tracking-wide text-[var(--fg-muted)]">
            From earlier phases
          </div>
          <ul className="space-y-1">
            {carried.map((c: CarriedAnswerView) => (
              <li key={c.key} className="text-sm">
                <span className="text-[var(--fg-muted)]">{c.key.replace(/_/g, " ")}:</span>{" "}
                {displayAnswer(c.answer)}
                {c.extractedAt ? (
                  <span className="ml-2 text-xs text-[var(--fg-muted)]">
                    {formatDateTime(c.extractedAt)}
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </Card>
  );
}
